import React, { useCallback, useEffect, useRef } from 'react';
import ProjectManagementPanel from './ProjectManagementPanel';
import { autoSaveProject } from '../services/modiqDataService';

const ProjectManagementWrapper = ({ 
  // Application state props
  parcelCoordinates,
  parcelWidth,
  parcelLength,
  setbackNorth,
  setbackEast,
  setbackSouth,
  setbackWest,
  setbackPoints,
  // Edge-based setback distances from MapBox component
  edgeSetbackDistances,
  setEdgeSetbackDistances,
  pcaLayoutStrategy,
  orientationOverride,
  principalAngle,
  boxWidth,
  boxLength,
  minBoxWidth,
  maxBoxWidth,
  minBoxLength,
  maxBoxLength,
  buildingPadding,
  floorCount,
  hasPodium,
  layoutState,
  internalPcaBuildingData,
  buildingSettings,
  colors,
  showDimensions,
  showGrid,
  gridSize,
  showLivableSpacesTable,
  unitQuantities,
  calculateProjectMetrics,
  
  // State setters for restoration
  setParcelCoordinates,
  setParcelWidth,
  setParcelLength,
  setSetbackNorth,
  setSetbackEast,
  setSetbackSouth,
  setSetbackWest,
  setSetbackPoints,
  setPcaLayoutStrategy,
  setOrientationOverride,
  setPrincipalAngle,
  setBoxWidth,
  setBoxLength,
  setMinBoxWidth,
  setMaxBoxWidth,
  setMinBoxLength,
  setMaxBoxLength,
  setBuildingPadding,
  setFloorCount,
  setHasPodium,
  setInternalPcaBuildingData,
  setBuildingSettings,
  setColors,
  setShowDimensions,
  setShowGrid,
  setGridSize,
  setShowLivableSpacesTable,
  setUnitQuantities,
  dispatch,
  ACTIONS,
  
  // Market and pricing data
  selectedMarket,
  setSelectedMarket,
  customRentOverrides,
  setCustomRentOverrides,
  
  // Project address
  projectAddress,
  setProjectAddress,
  
  // Layout mode (Density/Precision)
  layoutMode,
  setLayoutMode,
  startingCorner,
  setStartingCorner,
  precisionBoxCount,
  setPrecisionBoxCount,
  precisionOffset,
  setPrecisionOffset,
  precisionOffsetY,
  setPrecisionOffsetY,
  
  // URL-based project loading
  initialProjectId = null,
  onProjectChange = () => {}
}) => {
  
  // Track current project name for auto-save
  const currentProjectName = useRef(null);
  // Track current project ID for auto-save
  const currentProjectIdRef = useRef(null);
  
  // Sync currentProjectName with the hook's project name
  // This ensures auto-saves use the most current project name
  useEffect(() => {
    // We'll get the project name from the ProjectManagementPanel hook via a callback
    console.log('🔄 ProjectManagementWrapper currentProjectName.current:', currentProjectName.current);
    console.log('🔄 ProjectManagementWrapper currentProjectIdRef.current:', currentProjectIdRef.current);
  }, []);
  
  // Function to collect current application state for saving
  const getCurrentModIQState = useCallback(() => {
    const state = {
      // Include project name for auto-save if we have one
      ...(currentProjectName.current && { projectName: currentProjectName.current }),
      parcelCoordinates,
      parcelWidth,
      parcelLength,
      setbackNorth,
      setbackEast,
      setbackSouth,
      setbackWest,
      setbackPoints,
      edgeSetbackDistances,
      pcaLayoutStrategy,
      orientationOverride,
      principalAngle,
      boxWidth,
      boxLength,
      minBoxWidth,
      maxBoxWidth,
      minBoxLength,
      maxBoxLength,
      buildingPadding,
      floorCount,
      hasPodium,
      layoutState,
      buildableAreaCoords: internalPcaBuildingData?.buildableAreaCoords,
      pcaBuildingData: internalPcaBuildingData,
      buildingSettings,
      colors,
      showDimensions,
      showGrid,
      gridSize,
      showLivableSpacesTable,
      unitQuantities,
      projectMetrics: calculateProjectMetrics ? calculateProjectMetrics() : null,
      
      // Market and pricing data
      selectedMarket,
      customRentOverrides,
      
      // Project address
      projectAddress,
      
      // Layout mode (Density/Precision)
      layoutMode,
      startingCorner,
      precisionBoxCount,
      precisionOffset,
      precisionOffsetY
    };

    console.log('📊 getCurrentModIQState with projectName:', state.projectName);
    console.log('📊 Layout mode data being saved:', { layoutMode, startingCorner, precisionBoxCount, precisionOffset });
    console.log('📊 getCurrentModIQState orientationOverride value:', {
      value: orientationOverride,
      isNull: orientationOverride === null,
      isUndefined: orientationOverride === undefined,
      typeOf: typeof orientationOverride
    });
    return state;
  }, [
    parcelCoordinates,
    parcelWidth,
    parcelLength,
    setbackNorth,
    setbackEast,
    setbackSouth,
    setbackWest,
    setbackPoints,
    edgeSetbackDistances,
    pcaLayoutStrategy,
    orientationOverride,
    principalAngle,
    boxWidth,
    boxLength,
    minBoxWidth,
    maxBoxWidth,
    minBoxLength,
    maxBoxLength,
    buildingPadding,
    floorCount,
    hasPodium,
    layoutState,
    internalPcaBuildingData,
    buildingSettings,
    colors,
    showDimensions,
    showGrid,
    gridSize,
    showLivableSpacesTable,
    unitQuantities,
    calculateProjectMetrics,
    selectedMarket,
    customRentOverrides,
    projectAddress,
    layoutMode,
    startingCorner,
    precisionBoxCount,
    precisionOffset,
    precisionOffsetY
  ]);

  // Function to restore project data to application state
  const handleProjectLoaded = useCallback((projectData, projectId) => {
    console.log('🔄 Restoring project data to application state');
    console.log('🆔 Project ID for tracking:', projectId);
    console.log('🔍 Full project data keys:', Object.keys(projectData || {}));
    console.log('🔍 layoutMode in projectData:', projectData?.layoutMode);
    console.log('🔍 precisionBoxCount in projectData:', projectData?.precisionBoxCount);
    console.log('🔍 precisionOffset in projectData:', projectData?.precisionOffset);
    
    // CRITICAL: Set the currentProjectIdRef so subsequent saves update this project
    if (projectId) {
      currentProjectIdRef.current = projectId;
      console.log('📝 Set currentProjectIdRef to:', projectId);
    } else {
      console.warn('⚠️ No projectId provided - subsequent saves may create duplicates!');
    }
    
    console.log('🧭 ORIENTATION DEBUG - All orientation fields in loaded data:', {
      'projectData.orientationOverride': projectData.orientationOverride,
      'projectData.orientationAngle': projectData.orientationAngle, 
      'projectData.buildings?.orientationAngle': projectData.buildings?.orientationAngle,
      'projectData.buildings?.orientationOverride': projectData.buildings?.orientationOverride,
      'projectData.principalAngle': projectData.principalAngle,
      'projectData.buildings?.principalAngle': projectData.buildings?.principalAngle
    });
    
    try {
      // Store the loaded project name for auto-save preservation
      if (projectData.projectName) {
        currentProjectName.current = projectData.projectName;
        console.log('📝 Stored project name for auto-save:', projectData.projectName);
      }
      
      // Handle both flat structure (extractModIQState) and nested structure (saveModIQProject)
      
      // Restore basic parcel settings
      const parcelWidth = projectData.parcelWidth || projectData.parcel?.bounds?.width;
      const parcelLength = projectData.parcelLength || projectData.parcel?.bounds?.height;
      
      if (parcelWidth !== undefined && setParcelWidth) {
        console.log('Restoring parcel width:', parcelWidth);
        setParcelWidth(parcelWidth);
      }
      if (parcelLength !== undefined && setParcelLength) {
        console.log('Restoring parcel length:', parcelLength);
        setParcelLength(parcelLength);
      }
      
      // Restore building dimensions
      const boxWidth = projectData.boxWidth || projectData.buildings?.boxDimensions?.width;
      const boxLength = projectData.boxLength || projectData.buildings?.boxDimensions?.length;
      const buildingPadding = projectData.buildingPadding || projectData.buildings?.buildingPadding;
      
      if (boxWidth !== undefined && setBoxWidth) {
        console.log('Restoring box width:', boxWidth);
        setBoxWidth(boxWidth);
      }
      if (boxLength !== undefined && setBoxLength) {
        console.log('Restoring box length:', boxLength);
        setBoxLength(boxLength);
      }
      if (buildingPadding !== undefined && setBuildingPadding) {
        console.log('Restoring building padding:', buildingPadding);
        setBuildingPadding(buildingPadding);
      }
      
      // Restore setbacks
      const setbackNorth = projectData.setbackNorth || projectData.parcel?.setbacks?.north;
      const setbackEast = projectData.setbackEast || projectData.parcel?.setbacks?.east;
      const setbackSouth = projectData.setbackSouth || projectData.parcel?.setbacks?.south;
      const setbackWest = projectData.setbackWest || projectData.parcel?.setbacks?.west;
      
      if (setbackNorth !== undefined && setSetbackNorth) setSetbackNorth(setbackNorth);
      if (setbackEast !== undefined && setSetbackEast) setSetbackEast(setbackEast);
      if (setbackSouth !== undefined && setSetbackSouth) setSetbackSouth(setbackSouth);
      if (setbackWest !== undefined && setSetbackWest) setSetbackWest(setbackWest);
      
      // Restore box constraints
      const minBoxWidth = projectData.minBoxWidth || projectData.buildings?.boxDimensions?.constraints?.minWidth;
      const maxBoxWidth = projectData.maxBoxWidth || projectData.buildings?.boxDimensions?.constraints?.maxWidth;
      const minBoxLength = projectData.minBoxLength || projectData.buildings?.boxDimensions?.constraints?.minLength;
      const maxBoxLength = projectData.maxBoxLength || projectData.buildings?.boxDimensions?.constraints?.maxLength;
      
      if (minBoxWidth !== undefined && setMinBoxWidth) setMinBoxWidth(minBoxWidth);
      if (maxBoxWidth !== undefined && setMaxBoxWidth) setMaxBoxWidth(maxBoxWidth);
      if (minBoxLength !== undefined && setMinBoxLength) setMinBoxLength(minBoxLength);
      if (maxBoxLength !== undefined && setMaxBoxLength) setMaxBoxLength(maxBoxLength);
      
      // Restore floor settings
      const floorCount = projectData.floorCount || projectData.buildings?.floorCount;
      const hasPodium = projectData.hasPodium || projectData.buildings?.hasPodium;
      
      if (floorCount !== undefined && setFloorCount) {
        console.log('Restoring floor count:', floorCount);
        setFloorCount(floorCount);
      }
      if (hasPodium !== undefined && setHasPodium) {
        console.log('Restoring podium setting:', hasPodium);
        setHasPodium(hasPodium);
      }
      
      // Restore display settings
      const showDimensions = projectData.showDimensions || projectData.settings?.display?.showDimensions;
      const showGrid = projectData.showGrid || projectData.settings?.display?.showGrid;
      const gridSize = projectData.gridSize || projectData.settings?.display?.gridSize;
      const showLivableSpacesTable = projectData.showLivableSpacesTable || projectData.settings?.display?.showLivableSpacesTable;
      
      if (showDimensions !== undefined && setShowDimensions) setShowDimensions(showDimensions);
      if (showGrid !== undefined && setShowGrid) setShowGrid(showGrid);
      if (gridSize !== undefined && setGridSize) setGridSize(gridSize);
      if (showLivableSpacesTable !== undefined && setShowLivableSpacesTable) setShowLivableSpacesTable(showLivableSpacesTable);
      
      // Restore colors
      const colors = projectData.colors || projectData.settings?.colors;
      if (colors && setColors) {
        console.log('Restoring colors:', colors);
        setColors(colors);
      }
      
      // Restore unit quantities (the actual count of each unit type)
      // Note: unitMix in the data service refers to buildingSettings, NOT unitQuantities
      const unitQuantities = projectData.unitQuantities;
      if (unitQuantities && setUnitQuantities) {
        console.log('Restoring unit quantities:', unitQuantities);
        setUnitQuantities(unitQuantities);
      }
      
      // Restore market and pricing data
      const savedMarket = projectData.selectedMarket;
      const savedRentOverrides = projectData.customRentOverrides;
      
      if (savedMarket && setSelectedMarket) {
        console.log('🏪 Restoring selected market:', savedMarket);
        setSelectedMarket(savedMarket);
      }
      if (savedRentOverrides && setCustomRentOverrides) {
        console.log('💰 Restoring custom rent overrides:', savedRentOverrides);
        setCustomRentOverrides(savedRentOverrides);
      }
      
      // Restore project address
      const savedAddress = projectData.projectAddress;
      if (savedAddress && setProjectAddress) {
        console.log('📍 Restoring project address:', savedAddress);
        setProjectAddress(savedAddress);
      }
      
      // Restore layout mode (Density/Precision)
      console.log('📐 Layout mode data in loaded project:', { 
        layoutMode: projectData.layoutMode, 
        startingCorner: projectData.startingCorner,
        precisionBoxCount: projectData.precisionBoxCount,
        precisionOffset: projectData.precisionOffset,
        precisionOffsetY: projectData.precisionOffsetY,
        hasSetLayoutMode: !!setLayoutMode,
        hasSetStartingCorner: !!setStartingCorner,
        hasSetPrecisionBoxCount: !!setPrecisionBoxCount,
        hasSetPrecisionOffset: !!setPrecisionOffset,
        hasSetPrecisionOffsetY: !!setPrecisionOffsetY
      });
      
      // Always try to restore layout mode, with detailed logging
      console.log('📐 Attempting to restore layout mode. Value:', projectData.layoutMode, 'Setter exists:', !!setLayoutMode);
      if (setLayoutMode) {
        if (projectData.layoutMode) {
          console.log('📐 CALLING setLayoutMode with:', projectData.layoutMode);
          setLayoutMode(projectData.layoutMode);
        } else {
          console.log('📐 WARNING: layoutMode not found in project data');
        }
      } else {
        console.log('📐 ERROR: setLayoutMode is not available!');
      }
      
      if (setStartingCorner && projectData.startingCorner) {
        console.log('📐 CALLING setStartingCorner with:', projectData.startingCorner);
        setStartingCorner(projectData.startingCorner);
      }
      
      if (setPrecisionBoxCount) {
        console.log('📐 CALLING setPrecisionBoxCount with:', projectData.precisionBoxCount);
        setPrecisionBoxCount(projectData.precisionBoxCount !== undefined ? projectData.precisionBoxCount : null);
      }
      
      if (setPrecisionOffset) {
        console.log('📐 CALLING setPrecisionOffset with:', projectData.precisionOffset);
        setPrecisionOffset(projectData.precisionOffset !== undefined ? projectData.precisionOffset : 0);
      }
      
      if (setPrecisionOffsetY) {
        console.log('📐 CALLING setPrecisionOffsetY with:', projectData.precisionOffsetY);
        setPrecisionOffsetY(projectData.precisionOffsetY !== undefined ? projectData.precisionOffsetY : 0);
      }

      // Restore parcel coordinates and setback points - CRITICAL for MapBox display
      const parcelCoordinates = projectData.parcelCoordinates || projectData.parcel?.coordinates;
      const setbackPoints = projectData.setbackPoints || projectData.parcel?.setbacks?.points;
      const setbackDistances = projectData.setbackDistances || projectData.parcel?.setbacks?.distances;
      
      if (parcelCoordinates && setParcelCoordinates) {
        console.log('🗺️ Restoring parcel coordinates for MapBox:', parcelCoordinates);
        setParcelCoordinates(parcelCoordinates);
      }
      if (setbackPoints && setSetbackPoints) {
        console.log('🗺️ Restoring setback points:', setbackPoints);
        setSetbackPoints(setbackPoints);
      }
      if (setbackDistances && Array.isArray(setbackDistances)) {
        console.log('🗺️ Restoring setback distances:', setbackDistances);
        // We need to pass this to the MapBox component through props
      }
      
      // Restore edge setback distances
      const edgeSetbackDistances = projectData.edgeSetbackDistances || projectData.parcel?.setbacks?.edgeDistances;
      console.log('🔍 Looking for edge setback distances in project data:');
      console.log('  projectData.edgeSetbackDistances:', projectData.edgeSetbackDistances);
      console.log('  projectData.parcel?.setbacks?.edgeDistances:', projectData.parcel?.setbacks?.edgeDistances);
      console.log('  Final edgeSetbackDistances:', edgeSetbackDistances);
      
      if (edgeSetbackDistances && Array.isArray(edgeSetbackDistances)) {
        console.log('🗺️ Restoring edge setback distances:', edgeSetbackDistances);
        console.log('🗺️ setEdgeSetbackDistances function available:', !!setEdgeSetbackDistances);
        if (setEdgeSetbackDistances) {
          setEdgeSetbackDistances(edgeSetbackDistances);
          console.log('🗺️ Called setEdgeSetbackDistances with:', edgeSetbackDistances);
          // Add a timeout to check if the state actually updated
          setTimeout(() => {
            console.log('🔍 Checking if edgeSetbackDistances state was actually updated...');
          }, 100);
        }
      } else {
        console.warn('⚠️ No edge setback distances found in project data');
        console.log('🔍 Available project data keys:', Object.keys(projectData));
        console.log('🔍 Available parcel keys:', projectData.parcel ? Object.keys(projectData.parcel) : 'No parcel data');
        console.log('🔍 Available setbacks keys:', projectData.parcel?.setbacks ? Object.keys(projectData.parcel.setbacks) : 'No setbacks data');
        console.log('🔍 Full setbacks object:', projectData.parcel?.setbacks);
      }
      
      // Restore layout strategy and angles
      const layoutStrategy = projectData.pcaLayoutStrategy || projectData.buildings?.layoutStrategy;
      const orientationAngle = projectData.orientationOverride || projectData.orientationAngle || projectData.buildings?.orientationAngle || projectData.buildings?.orientationOverride;
      const principalAngle = projectData.principalAngle || projectData.buildings?.principalAngle;
      
      console.log('🔍 Looking for orientation data in project:');
      console.log('  projectData.orientationOverride:', projectData.orientationOverride);
      console.log('  projectData.orientationAngle (legacy):', projectData.orientationAngle);
      console.log('  projectData.buildings?.orientationAngle:', projectData.buildings?.orientationAngle);
      console.log('  projectData.buildings?.orientationOverride:', projectData.buildings?.orientationOverride);
      console.log('  Final orientationAngle:', orientationAngle);
      console.log('  Current orientationOverride state before restore:', orientationOverride);
      
      if (layoutStrategy && setPcaLayoutStrategy) {
        console.log('Restoring layout strategy:', layoutStrategy);
        setPcaLayoutStrategy(layoutStrategy);
      }
      if (orientationAngle !== undefined && orientationAngle !== null && setOrientationOverride) {
        console.log('🧭 Restoring orientation angle:', orientationAngle);
        console.log('🧭 Calling setOrientationOverride with value:', orientationAngle, 'type:', typeof orientationAngle);
        setOrientationOverride(orientationAngle);
        setTimeout(() => {
          console.log('🔍 Checking if orientationOverride state was actually updated...');
          console.log('🔍 Current orientationOverride value after restoration attempt:', orientationOverride);
        }, 100);
      } else {
        console.warn('⚠️ No orientation angle found in project data or value is undefined/null');
        console.log('🔍 orientationAngle value details:', {
          value: orientationAngle,
          isUndefined: orientationAngle === undefined,
          isNull: orientationAngle === null,
          typeOf: typeof orientationAngle
        });
        console.log('🔍 Available orientation data:', {
          'projectData.orientationOverride': projectData.orientationOverride,
          'projectData.orientationAngle (legacy)': projectData.orientationAngle,
          'projectData.buildings?.orientationAngle': projectData.buildings?.orientationAngle,
          'projectData.buildings?.orientationOverride': projectData.buildings?.orientationOverride
        });
      }
      if (principalAngle !== undefined && setPrincipalAngle) {
        console.log('Restoring principal angle:', principalAngle);
        setPrincipalAngle(principalAngle);
      }
      
      // Helper function to ensure data is an array (handles improperly deserialized data)
      const ensureArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        // Handle object with numeric keys (improperly deserialized array)
        if (typeof data === 'object') {
          // Check if it's a serialized array format
          if (data.__isArray && data.__data) {
            const arr = new Array(data.__length || 0);
            Object.keys(data.__data).forEach(key => {
              arr[parseInt(key)] = data.__data[key];
            });
            return arr;
          }
          // Try to convert object with numeric keys to array
          const keys = Object.keys(data);
          if (keys.length > 0 && keys.every(k => !isNaN(parseInt(k)))) {
            const maxIndex = Math.max(...keys.map(k => parseInt(k)));
            const arr = new Array(maxIndex + 1);
            keys.forEach(k => {
              arr[parseInt(k)] = data[k];
            });
            return arr.filter(item => item !== undefined);
          }
          // If it looks like a single object, wrap it in array
          if (data.id !== undefined || data.buildPathId !== undefined) {
            return [data];
          }
        }
        console.warn('⚠️ Could not convert to array:', data);
        return [];
      };

      // Restore layout state (buildings and boxes) - CRITICAL for building display
      const rawBuildPaths = projectData.buildPaths || projectData.buildings?.buildPaths || projectData.layoutState?.buildPaths;
      const rawBoxes = projectData.boxes || projectData.buildings?.boxes || projectData.layoutState?.boxes;
      const buildPaths = ensureArray(rawBuildPaths);
      const boxes = ensureArray(rawBoxes);
      const selectedOptimizations = projectData.selectedOptimizations || projectData.optimizations?.selectedOptimizations || projectData.layoutState?.selectedOptimizations;
      const buildingOptimizations = projectData.buildingOptimizations || projectData.optimizations?.buildingOptimizations || projectData.layoutState?.buildingOptimizations;
      const selectedBuildingId = projectData.selectedBuildingId || projectData.optimizations?.selectedBuildingId || projectData.layoutState?.selectedBuildingId;
      
      console.log('🔍 Looking for building data in project:');
      console.log('  projectData.buildPaths:', projectData.buildPaths, 'isArray:', Array.isArray(projectData.buildPaths));
      console.log('  projectData.buildings?.buildPaths:', projectData.buildings?.buildPaths, 'isArray:', Array.isArray(projectData.buildings?.buildPaths));
      console.log('  projectData.layoutState?.buildPaths:', projectData.layoutState?.buildPaths, 'isArray:', Array.isArray(projectData.layoutState?.buildPaths));
      console.log('  Final buildPaths (after ensureArray):', buildPaths?.length || 0, 'isArray:', Array.isArray(buildPaths));
      console.log('  projectData.boxes:', projectData.boxes?.length || 0);
      console.log('  projectData.buildings?.boxes:', projectData.buildings?.boxes?.length || 0);
      console.log('  projectData.layoutState?.boxes:', projectData.layoutState?.boxes?.length || 0);
      console.log('  Final boxes (after ensureArray):', boxes?.length || 0, 'isArray:', Array.isArray(boxes));
      
      if ((buildPaths.length > 0 || boxes.length > 0) && dispatch && ACTIONS) {
        const newLayoutState = {
          buildPaths: buildPaths,
          boxes: boxes,
          selectedOptimizations: selectedOptimizations || {},
          buildingOptimizations: buildingOptimizations || {},
          selectedBuildingId: selectedBuildingId || null
        };
        console.log('🏗️ Restoring layout state (buildings):', {
          buildPathsCount: newLayoutState.buildPaths.length,
          boxesCount: newLayoutState.boxes.length,
          buildPaths: newLayoutState.buildPaths,
          boxes: newLayoutState.boxes
        });
        dispatch({ type: ACTIONS.SET_LAYOUT, payload: newLayoutState });
      } else {
        console.warn('⚠️ No building data found in project or missing dispatch/ACTIONS');
        console.log('🔍 Available for building restoration:', {
          hasBuildPaths: buildPaths.length > 0,
          hasBoxes: boxes.length > 0,
          hasDispatch: !!dispatch,
          hasACTIONS: !!ACTIONS
        });
      }
      
      // Restore PCA building data - CRITICAL for complex building layouts
      const pcaBuildingData = projectData.pcaBuildingData || projectData.buildings?.pcaBuildingData;
      if (pcaBuildingData && setInternalPcaBuildingData) {
        console.log('🏗️ Restoring PCA building data:', pcaBuildingData);
        setInternalPcaBuildingData(pcaBuildingData);
      }
      
      // Restore building settings - CRITICAL for per-building configurations
      // Note: buildingSettings is saved as 'unitMix' in the data service, so check both paths
      const buildingSettings = projectData.buildingSettings || projectData.unitMix || projectData.buildings?.buildingSettings;
      if (buildingSettings && setBuildingSettings) {
        console.log('🏗️ Restoring building settings:', buildingSettings);
        console.log('🏗️ Building settings keys:', Object.keys(buildingSettings));
        // Log the first building's settings to verify structure
        const firstBuildingId = Object.keys(buildingSettings)[0];
        if (firstBuildingId) {
          console.log('🏗️ First building settings structure:', {
            buildingId: firstBuildingId,
            hasUnitTypeDistribution: !!buildingSettings[firstBuildingId]?.unitTypeDistribution,
            hasBusinessModelDistribution: !!buildingSettings[firstBuildingId]?.businessModelDistribution,
            businessModelDistribution: buildingSettings[firstBuildingId]?.businessModelDistribution
          });
        }
        setBuildingSettings(buildingSettings);
      } else {
        console.log('⚠️ No building settings found in project data');
        console.log('🔍 Available paths checked:', {
          'projectData.buildingSettings': !!projectData.buildingSettings,
          'projectData.unitMix': !!projectData.unitMix,
          'projectData.buildings?.buildingSettings': !!projectData.buildings?.buildingSettings
        });
      }
      
      console.log('✅ Project data successfully restored to application state');
      
      // Disable auto-save for 5 seconds after loading to prevent overwriting
      autoSaveDisabledUntil.current = Date.now() + 5000;
      console.log('🚫 Auto-save disabled for 5 seconds after project load');
      
      // Show success message in console instead of popup
      console.log(`🎉 Project "${projectData.projectName}" loaded successfully!`);
      console.log('🔍 Final project name after load:', projectData.projectName);
      
    } catch (error) {
      console.error('❌ Error restoring project data:', error);
      console.error('Failed to load project data:', error.message);
    }
  }, [
    setParcelWidth,
    setParcelLength,
    setBoxWidth,
    setBoxLength,
    setBuildingPadding,
    setSetbackNorth,
    setSetbackEast,
    setSetbackSouth,
    setSetbackWest,
    setMinBoxWidth,
    setMaxBoxWidth,
    setMinBoxLength,
    setMaxBoxLength,
    setFloorCount,
    setHasPodium,
    setShowDimensions,
    setShowGrid,
    setGridSize,
    setShowLivableSpacesTable,
    setColors,
    setUnitQuantities,
    setParcelCoordinates,
    setSetbackPoints,
    setPcaLayoutStrategy,
    setOrientationOverride,
    setPrincipalAngle,
    setInternalPcaBuildingData,
    dispatch,
    ACTIONS,
    setEdgeSetbackDistances,
    setBuildingSettings,
    setSelectedMarket,
    setCustomRentOverrides,
    setProjectAddress,
    setLayoutMode,
    setStartingCorner,
    setPrecisionBoxCount,
    setPrecisionOffset,
    setPrecisionOffsetY
  ]);

  // Auto-save functionality
  const lastAutoSaveState = useRef(null);
  const autoSaveDisabledUntil = useRef(null); // Prevent auto-save briefly after loading
  
  const hasSignificantChanges = useCallback(() => {
    // Only auto-save if we have meaningful content (not just default values)
    return !!(
      parcelCoordinates || 
      (layoutState.buildPaths && layoutState.buildPaths.length > 0) ||
      (layoutState.boxes && layoutState.boxes.length > 0) ||
      parcelWidth !== 200 || 
      parcelLength !== 150 ||
      boxWidth !== 13.7 ||
      boxLength !== 55 ||
      buildingPadding !== 20 ||
      floorCount !== 1 ||
      hasPodium
    );
  }, [
    parcelCoordinates, 
    layoutState, 
    parcelWidth, 
    parcelLength, 
    boxWidth, 
    boxLength, 
    buildingPadding, 
    floorCount, 
    hasPodium
  ]);

  // Auto-save effect - triggers when significant state changes
  // IMPORTANT: Only auto-save when we have an existing project ID to prevent creating garbage files
  useEffect(() => {
    // Skip auto-save if no project ID (user hasn't saved the project yet)
    if (!currentProjectIdRef.current) {
      // Don't log this every time - it's expected for new/unsaved projects
      return;
    }
    
    // Skip auto-save if disabled (recently loaded a project)
    if (autoSaveDisabledUntil.current && Date.now() < autoSaveDisabledUntil.current) {
      console.log('🚫 Auto-save temporarily disabled after project load');
      return;
    }
    
    if (!hasSignificantChanges()) {
      return; // Don't auto-save empty/default projects
    }

    const currentState = getCurrentModIQState();
    const currentStateString = JSON.stringify(currentState);
    
    // Only trigger auto-save if state actually changed
    if (lastAutoSaveState.current !== currentStateString) {
      lastAutoSaveState.current = currentStateString;
      
      // Trigger auto-save with debouncing (2 second delay)
      autoSaveProject(
        currentState, 
        currentProjectIdRef.current, // Use tracked project ID for auto-save
        'anonymous', 
        2000 // 2 second delay
      );
      
      console.log('🔄 Auto-save triggered for existing project:', currentProjectIdRef.current);
    }
  }, [
    getCurrentModIQState, 
    hasSignificantChanges,
    // Key dependencies that should trigger auto-save
    parcelCoordinates,
    parcelWidth,
    parcelLength,
    boxWidth,
    boxLength,
    buildingPadding,
    floorCount,
    hasPodium,
    layoutState.buildPaths,
    layoutState.boxes,
    internalPcaBuildingData,
    // Layout mode dependencies
    layoutMode,
    startingCorner,
    precisionBoxCount,
    precisionOffset,
    precisionOffsetY
  ]);

  return (
    <ProjectManagementPanel 
      currentModIQState={getCurrentModIQState()}
      onProjectLoaded={handleProjectLoaded}
      onProjectNameUpdate={(projectName) => {
        console.log('📝 Project name updated in wrapper:', projectName);
        currentProjectName.current = projectName;
      }}
      onProjectIdUpdate={(projectId) => {
        console.log('📝 Project ID updated in wrapper:', projectId);
        currentProjectIdRef.current = projectId;
        // Update URL when project changes
        onProjectChange(projectId);
      }}
      projectAddress={projectAddress}
      initialProjectId={initialProjectId}
    />
  );
};

export default ProjectManagementWrapper; 